import { useEffect, useRef, useState } from "react";
import { Autocomplete, TextField, CircularProgress, AutocompleteProps } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchBienDataByNameOrByGroupeName, clearBienData } from "../../reduxStore/BienSlice";
import { RootState, AppDispatch } from '../../reduxStore/Store';
import { fromEvent } from "rxjs";
import { debounceTime, switchMap } from "rxjs/operators";
import { Bien } from "../../models/models";
import { labels } from "../../constants/labels";

interface BienAutoCProps 
extends Partial<AutocompleteProps<Bien, false, false, false>> {
  
  onBienChange: (bien: Bien | null) => void;
  selectedBien?: Bien | null;
}

const limit = 10; 
const offset = 0; 

const BienAutoC: React.FC<BienAutoCProps> = ({ onBienChange,selectedBien,...autocompleteProps }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { bienData, loading } = useSelector((state: RootState) => state.BienSlice);
  const [bien, setBien] = useState<Bien | null>(null);
  
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (selectedBien) {
      setBien(selectedBien);
    }
  }, [selectedBien]);

  useEffect(() => {
    if (inputRef.current) {
      const subscription = fromEvent(inputRef.current, "input")
        .pipe(
          debounceTime(500),
          switchMap((event: any) => {
            const nom = event.target.value.trim();
            if (nom.length > 2) {
              dispatch(fetchBienDataByNameOrByGroupeName({nom,limit, offset }));
            }
            return [];
          })
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [dispatch]);

  const handleChange = (_: any, newValue: Bien | null) => {
    setBien(newValue);
    onBienChange(newValue);
  };

  return (
    <Autocomplete
      options={bienData}
      value={bien} 
      getOptionLabel={(option: Bien) => `${option.nom} - ${option.groupe_bien?.nom ?? "N/A"}`}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      loading={loading}
      onChange={handleChange}
      onBlur={() => dispatch(clearBienData())} 
      {...autocompleteProps}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef} 
          label={labels.bien}
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          size="small"
        />
      )}
    />
  );
};

export default BienAutoC;
